/**
 * Centralized admin design tokens.
 *
 * The admin dashboard uses a single fixed brand color (no gradients) plus a
 * uniform set of semantic colors. Every admin surface should reference these
 * tokens instead of hardcoding hex/gradient values so the theme stays
 * consistent and is easy to retune in one place.
 */

const buildColor = ({ main, dark, light, contrastText = '#ffffff', rgb }) => ({
  main,
  dark,
  light,
  contrastText,
  // Tinted surfaces (chips, hover states, soft backgrounds).
  soft: `rgba(${rgb}, 0.08)`,
  softHover: `rgba(${rgb}, 0.12)`,
  border: `rgba(${rgb}, 0.24)`,
  // Elevation shadow tinted with the color.
  shadow: `rgba(${rgb}, 0.3)`,
});

export const BRAND = buildColor({
  main: '#667eea',
  dark: '#5568d3',
  light: '#8b9cf2',
  rgb: '102, 126, 234',
});

export const SUCCESS = buildColor({
  main: '#10b981',
  dark: '#059669',
  light: '#34d399',
  rgb: '16, 185, 129',
});

export const ERROR = buildColor({
  main: '#ef4444',
  dark: '#dc2626',
  light: '#f87171',
  rgb: '239, 68, 68',
});

export const WARNING = buildColor({
  main: '#f59e0b',
  dark: '#d97706',
  light: '#fbbf24',
  rgb: '245, 158, 11',
});

export const INFO = buildColor({
  main: '#3b82f6',
  dark: '#2563eb',
  light: '#60a5fa',
  rgb: '59, 130, 246',
});

export const NEUTRAL = {
  text: '#1f2937',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  divider: 'rgba(0, 0, 0, 0.08)',
  surface: '#ffffff',
  page: '#f8fafc',
};

/** Standard elevation shadow used by admin cards. */
export const CARD_SHADOW = '0 10px 30px rgba(0, 0, 0, 0.08)';
export const CARD_SHADOW_HOVER = '0 20px 40px rgba(0, 0, 0, 0.12)';

/**
 * Resolve a semantic status name to its token. Falls back to the brand color
 * for unknown statuses so callers never render an undefined background.
 */
export const STATUS_COLORS = {
  success: SUCCESS,
  error: ERROR,
  warning: WARNING,
  info: INFO,
  primary: BRAND,
};

export const ADMIN_COLORS = {
  brand: BRAND,
  success: SUCCESS,
  error: ERROR,
  warning: WARNING,
  info: INFO,
  neutral: NEUTRAL,
};

export default ADMIN_COLORS;
